/**
 * Страница кампаний рассылок: список кампаний с статусами и статистикой
 * Кнопка создания видна только при наличии permission campaigns:create
 * Дата: 26.01.2026 - Добавлено frontend логирование (TODO-034)
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/hooks/useCampaigns.ts - хук для управления состоянием кампаний
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * 
 * КАК РАБОТАЕТ:
 * - Отображает список кампаний с статусами и статистикой
 * - Создание/редактирование кампаний (через permissions)
 * 
 * СХЕМА:
 * CampaignsPage → useCampaigns → campaignsApi → backend API
 */
import { useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Table, TableHead, TableBody, TableRow, TableHeader, TableCell, EmptyState, LoadingState } from '@/components/ui';
import { useCampaigns } from '@/hooks/useCampaigns';
import { Plus } from 'lucide-react';
import { Can } from '@/components/Can';
import { formatDate } from '@/utils/formatters';
import { logger } from '@/utils/logger';

export function CampaignsPage() {
  const { campaigns, isLoading } = useCampaigns();

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'campaigns' });
  }, []);

  // Логирование загрузки списка кампаний
  useEffect(() => {
    if (!isLoading && campaigns) {
      logger.info('campaigns_list_loaded', { 
        count: campaigns.length 
      });
    }
  }, [isLoading, campaigns]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'running':
        return <Badge variant="info">Запущена</Badge>;
      case 'completed':
        return <Badge variant="success">Завершена</Badge>;
      case 'paused':
        return <Badge variant="warning">Приостановлена</Badge>;
      case 'cancelled':
        return <Badge variant="error">Отменена</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <MainLayout 
      headerTitle="Рассылки"
      headerActions={
        <Can permission="campaigns:create">
          <Button testId="btn-create-campaign">
            <Plus className="w-4 h-4 mr-2" />
            Создать кампанию
          </Button>
        </Can>
      }
    >
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Список кампаний</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingState />
            ) : campaigns.length === 0 ? (
              <EmptyState 
                type="noData"
                title="Нет кампаний"
                description="Создайте первую кампанию"
                actionText="Создать кампанию"
                onAction={() => {/* TODO: открыть модалку создания */}}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Название</TableHeader>
                    <TableHeader>Тип</TableHeader>
                    <TableHeader>Статус</TableHeader>
                    <TableHeader>Статистика</TableHeader>
                    <TableHeader>Создана</TableHeader>
                    <TableHeader>Действия</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id} testId={`row-campaign-${campaign.id}`}>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>{campaign.type}</TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>Отправлено: {campaign.stats.sent}</div>
                          <div>Доставлено: {campaign.stats.delivered}</div>
                          <div>Ошибок: {campaign.stats.failed}</div>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(campaign.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Can permission="campaigns:update">
                            <Button size="sm" variant="secondary" testId={`btn-edit-campaign-${campaign.id}`}>
                              Редактировать
                            </Button>
                          </Can>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}

